import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#050508] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white mb-6 transition-colors text-sm">
          <ArrowLeft size={14} /><span className="font-body">Back to Home</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#007AFF]/15 flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#007AFF]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">Terms of Service</h1>
              <p className="font-body text-white/40 text-sm">Last updated: August 2026</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 md:p-8 space-y-6">
            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">1. Acceptance of Terms</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                By accessing or using Monk Algo's website and services, including Monk Algo software, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">2. Description of Services</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Monk Algo provides subscription-based trading software, including TradingView indicators and strategies (Pine Script), MetaTrader 5 Expert Advisors, and NinjaTrader 8 strategy files. Our tools plot technical levels, ranges, and breakout information on the user's charts, and our automated versions can execute rule-based trades on the customer's own trading account at their discretion. Monk Algo is a software provider only — we do not provide trading tips, signals, or investment advice, we do not manage or handle client funds, and we do not execute trades on anyone's behalf.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">3. Subscription and Payments</h2>
              <ul className="space-y-2">
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  We offer a Quarterly plan at ₹49,997 (billed every 3 months) and a Yearly plan at ₹1,49,997 (billed annually)
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  Each subscription includes license activation on up to 2 trading accounts
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  Payments are processed securely through Razorpay
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#10B981] mt-1">•</span>
                  Subscriptions renew automatically unless cancelled before the renewal date
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">4. License Grant</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Upon successful subscription, we grant you a limited, non-exclusive, non-transferable license to use Monk Algo software on the licensed number of trading accounts across supported platforms (TradingView, MetaTrader 5, NinjaTrader 8). This license is for personal use only and may not be sublicensed, sold, or distributed.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">5. Prohibited Uses</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-3">You agree not to:</p>
              <ul className="space-y-2">
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#EF4444] mt-1">•</span>
                  Reverse engineer, decompile, or disassemble our software
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#EF4444] mt-1">•</span>
                  Share, distribute, or resell our software to third parties
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#EF4444] mt-1">•</span>
                  Use our software on more than the licensed number of accounts
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#EF4444] mt-1">•</span>
                  Modify or create derivative works from our software
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">6. Risk Disclaimer</h2>
              <div className="p-4 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-3">
                <p className="font-body text-[#F59E0B] text-sm leading-relaxed font-medium">
                  IMPORTANT: Trading in financial markets involves substantial risk of loss and is not suitable for all investors.
                </p>
              </div>
              <ul className="space-y-2">
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#F59E0B] mt-1">•</span>
                  Past performance (including backtest results) does not guarantee future results
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#F59E0B] mt-1">•</span>
                  You may lose some or all of your invested capital
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#F59E0B] mt-1">•</span>
                  Only trade with money you can afford to lose
                </li>
                <li className="font-body text-white/60 text-sm flex items-start gap-2">
                  <span className="text-[#F59E0B] mt-1">•</span>
                  Monk Algo is not responsible for any trading losses
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">7. Limitation of Liability</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                To the maximum extent permitted by law, Monk Algo shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or trading losses, arising from your use of our services.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">8. Service Availability</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                We strive to maintain continuous service availability but do not guarantee uninterrupted access. We reserve the right to modify, suspend, or discontinue any part of our services with reasonable notice.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">9. Intellectual Property</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                All content, software, and materials on this website, including Monk Algo software, are the intellectual property of Monk Algo and are protected by applicable copyright and trademark laws.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">10. Termination</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                We reserve the right to terminate or suspend your access to our services at any time for violation of these terms. Upon termination, your license to use our software will be revoked immediately.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">11. Governing Law</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                These Terms of Service shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in India.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-lg text-white mb-3">12. Contact Information</h2>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-3 p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="font-body text-white/70 text-sm">Email: support@monkalgo.com</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
